import { useCallback, useContext, useState, useEffect } from 'react';
import { MultiChannelAudioUploadField } from '@scale/shared/types/params/textcollection';
import { MultiChannelAudioUploadFieldAnnotation } from '@scale/shared/types/response/textcollection';
import apiFetch from 'react-scale/lib/api-fetch';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import { Typography } from '@scale/scaleui';
import logger from 'react-scale/lib/logging';
import useCDSMultipartUpload from 'react-scale/components/scalers/tasks/textcollection/hooks/useCDSMultipartUpload';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  SupportedAudioFormat,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
  formatMaybeErrorForLogging,
} from '@scale/mm-shared';
import { processAudioAttachment } from 'react-scale/lib/chat-api/multimodal';
import retry from '@scale/shared/utils/retry';
import AudioUploadSection, {
  AudioUploadErrorMessage,
} from 'react-scale/components/scalers/tasks/textcollection/MultiChannelAudioUpload/AudioUploadSection';
import CombineAudioSection from 'react-scale/components/scalers/tasks/textcollection/MultiChannelAudioUpload/CombineAudioSection';
import { decodeAudioBuffer } from '@scale/frontend-lib/audio/decodeAudio';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const MOCK_ASSIGNMENT_ID = '664c627bac1e2188ffaf6621';

interface MultiChannelAudioUploadFieldSectionProps {
  field: MultiChannelAudioUploadField;
  annotation: MultiChannelAudioUploadFieldAnnotation;
  setAnnotation: (value: MultiChannelAudioUploadFieldAnnotation) => void;
  disabled?: boolean;
  assignmentId?: string;
}

export default function MultiChannelAudioUploadFieldSection(
  props: MultiChannelAudioUploadFieldSectionProps,
) {
  const { field, annotation, setAnnotation, disabled } = props;
  const taskContext = useContext(ChatTaskContext);
  const multipartUploadHook = useCDSMultipartUpload(
    props.assignmentId ?? taskContext.assignmentId ?? MOCK_ASSIGNMENT_ID,
    taskContext.workerId,
  );

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId: taskContext.projectId },
    false,
  );

  // Determine audio upload format from taxonomy
  const targetAudioFormat: SupportedAudioFormat | undefined = useChatStoreExtract(
    state => state.config.audio?.format,
  );

  // Use local annotation state to avoid stale update issues
  const [localAnnotation, setLocalAnnotation] = useState(annotation);

  // Update the global annotation state to reflect local changes
  useEffect(() => {
    setAnnotation(localAnnotation);
  }, [localAnnotation]);

  const handleFileUpload = useCallback(
    async (
      file: File,
      setProgress: (progress: number | null) => void,
      setUploading: (uploading: boolean) => void,
      setFileUrl: (url: string | undefined) => void,
      setErrorMessage: (message: AudioUploadErrorMessage | undefined) => void,
      fileNumber: 1 | 2,
      isRetry?: boolean,
    ) => {
      setErrorMessage(undefined);
      setUploading(true);

      try {
        /**
         * file type that CDS will convert into (audio only right now)
         */
        const fileType: SupportedAudioFormat | undefined = targetAudioFormat;

        const audioBuffer = await decodeAudioBuffer(await file.arrayBuffer());

        const upload = isRetry ? multipartUploadHook.retryUpload : multipartUploadHook.upload;

        const { cdsUrl } = await upload({
          file,
          fileType,
          onProgress: (progress: number) => setProgress(progress),
          onError: (message: string) => {
            setProgress(null);
            throw new Error(message);
          },
          onComplete: () => setProgress(null),
        });

        const { url } = await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
          method: 'POST',
          body: {
            url: cdsUrl,
          },
        });
        setFileUrl(url);

        setLocalAnnotation(prevAnnotation => ({
          ...prevAnnotation,
          response: [
            fileNumber === 1
              ? {
                  ...(prevAnnotation.response?.[0] ?? {}),
                  file1Url: cdsUrl,
                  file1Name: file.name,
                  mimeType: file.type,
                  duration: audioBuffer?.duration,
                }
              : {
                  ...(prevAnnotation.response?.[0] ?? {}),
                  file2Url: cdsUrl,
                  file2Name: file.name,
                  mimeType: file.type,
                  duration: audioBuffer?.duration,
                },
          ],
        }));

        try {
          // get bg noise data, transcripts, etc for their upload
          await retry(
            () =>
              processAudioAttachment({
                audioUrl: cdsUrl,
                audioFormat: targetAudioFormat,
                assignmentId: taskContext.assignmentId,
                workerId: taskContext.workerId,
                userGeneratedAudio: true,
                transcriptionService: shouldUseWhisperTranscription
                  ? createWhisperTranscriptionService()
                  : createAwsTranscriptionService(),
              }),
            { limit: 3 },
          );
        } catch (error) {
          logger.error(`FileUploadFieldSection: Failed to start processing audio attachment`, {
            projectId: taskContext.projectId,
            assignmentId: taskContext.assignmentId,
            workerId: taskContext.workerId,
            error: formatMaybeErrorForLogging(error),
          });
        }
      } catch (e) {
        setErrorMessage(AudioUploadErrorMessage.ErrorUploadingFile);
      } finally {
        setUploading(false);
      }
    },
    [
      multipartUploadHook,
      targetAudioFormat,
      taskContext,
      setLocalAnnotation,
      shouldUseWhisperTranscription,
    ],
  );

  const handleUploadRetry = useCallback(
    async (
      file: File,
      setProgress: (progress: number | null) => void,
      setUploading: (uploading: boolean) => void,
      setFileUrl: (url: string | undefined) => void,
      setErrorMessage: (message: AudioUploadErrorMessage | undefined) => void,
      fileNumber: 1 | 2,
    ) => {
      return await handleFileUpload(
        file,
        setProgress,
        setUploading,
        setFileUrl,
        setErrorMessage,
        fileNumber,
        true,
      );
    },
    [handleFileUpload],
  );

  const handleCancel = useCallback(
    (fileNumber: 1 | 2) => {
      if (fileNumber === 1) {
        setFile1(null);
        setFile1Url(undefined);
      } else {
        setFile2(null);
        setFile2Url(undefined);
      }
      setLocalAnnotation(prevAnnotation => ({
        ...prevAnnotation,
        response: [
          fileNumber === 1
            ? {
                ...(prevAnnotation.response?.[0] ?? {}),
                file1Url: undefined,
                file1Name: undefined,
                combinedFileUrl: undefined,
                combinedFileName: undefined,
              }
            : {
                ...(prevAnnotation.response?.[0] ?? {}),
                file2Url: undefined,
                file2Name: undefined,
                combinedFileUrl: undefined,
                combinedFileName: undefined,
              },
        ],
      }));
    },
    [setLocalAnnotation],
  );

  const [file1, setFile1] = useState<File | null>(null);
  const [file1Url, setFile1Url] = useState<string | undefined>(annotation.response[0]?.file1Url);
  const [file2, setFile2] = useState<File | null>(null);
  const [file2Url, setFile2Url] = useState<string | undefined>(annotation.response[0]?.file2Url);

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader title={field.title} description={field.description} hint={field.hint} />
      <Typography.Paragraph2 className="font-semibold">Audio file 1</Typography.Paragraph2>
      <AudioUploadSection
        file={file1}
        setFile={setFile1}
        fileName={file1?.name ?? annotation.response[0]?.file1Name}
        fileUrl={file1Url}
        mimeType={file1?.type ?? annotation.response[0]?.mimeType}
        disabled={disabled}
        setFileUrl={setFile1Url}
        handleFileUpload={(file, setProgress, setUploading, setFileUrl, setErrorMessage) =>
          handleFileUpload(file, setProgress, setUploading, setFileUrl, setErrorMessage, 1)
        }
        handleCancel={() => handleCancel(1)}
        handleUploadRetry={(file, setProgress, setUploading, setFileUrl, setErrorMessage) =>
          handleUploadRetry(file, setProgress, setUploading, setFileUrl, setErrorMessage, 1)
        }
      />
      <div className="h-2" />
      <Typography.Paragraph2 className="font-semibold">Audio file 2</Typography.Paragraph2>
      <AudioUploadSection
        file={file2}
        setFile={setFile2}
        fileName={file2?.name ?? annotation.response[0]?.file2Name}
        fileUrl={file2Url}
        mimeType={file2?.type ?? annotation.response[0]?.mimeType}
        disabled={disabled}
        setFileUrl={setFile2Url}
        handleFileUpload={(file, setProgress, setUploading, setFileUrl, setErrorMessage) =>
          handleFileUpload(file, setProgress, setUploading, setFileUrl, setErrorMessage, 2)
        }
        handleCancel={() => handleCancel(2)}
        handleUploadRetry={(file, setProgress, setUploading, setFileUrl, setErrorMessage) =>
          handleUploadRetry(file, setProgress, setUploading, setFileUrl, setErrorMessage, 2)
        }
      />
      {file1Url && file2Url && (
        <>
          <div className="h-2" />
          <CombineAudioSection
            annotation={annotation}
            setAnnotation={setAnnotation}
            file1Url={file1Url}
            file2Url={file2Url}
            setFile1Url={setFile1Url}
            setFile2Url={setFile2Url}
            readonly={disabled}
            assignmentContext={{
              assignmentId: taskContext.assignmentId ?? MOCK_ASSIGNMENT_ID,
              workerId: taskContext.workerId,
              projectId: taskContext.projectId,
            }}
          />
        </>
      )}
    </div>
  );
}
